import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router } from "react-router-dom";
import { Provider } from "react-redux";
import "./index.css";
import store from "./redux/store";
import { Theme } from "./features/theme/Theme";
import { initSiteConfig } from "./utils/siteConfig";
import envDefaults from "./utils/envDefaults";
import * as serviceWorker from "./serviceWorker";
import swDev from "./swDev";
import "typeface-poppins";
import axios from "axios";

const rootElement = document.getElementById("root");
rootElement.innerHTML =
  '<div style="display:flex;justify-content:center;align-items:center;height:100vh"><p>Loading...</p></div>';

if (window.location.pathname === "/mobile" && window.location.hash) {
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`
  );
}

const newApiBaseUrl = process.env.REACT_APP_NODEJS_API_BASE_URL;
const apiKey = process.env.REACT_APP_NODEJS_API_KEY;
const siteDomain =
  process.env.REACT_APP_SITE_DOMAIN_OVERRIDE || window.location.hostname;
const DEFAULT_SITE_ID = 248;

const isNodeApiRequest = (url = "") => {
  if (!url) return false;
  if (url.startsWith("/v1/")) return true;
  if (newApiBaseUrl && url.startsWith(newApiBaseUrl)) return true;
  return false;
};

axios.interceptors.request.use((config) => {
  const requestUrl = config?.url || "";
  if (apiKey && isNodeApiRequest(requestUrl)) {
    config.headers = config.headers || {};
    if (!config.headers["x-api-key"] && !config.headers["X-API-KEY"]) {
      config.headers["x-api-key"] = apiKey;
    }
  }
  return config;
});


async function fetchSiteConfig(url) {
  const res = await fetch(url, { headers: { "x-api-key": apiKey } });
  if (!res.ok) throw new Error(`Config API returned ${res.status}`);
  const json = await res.json();
  return json.config || json;
}

function updateSiteAssets(config) {
  const mediaSrc = config.REACT_APP_MEDIA_SRC || "";
  const siteName = config.REACT_APP_RESTAURANT_NAME || "";
  const siteUrl = config.REACT_APP_SITE || "";
  const aboutUs = config.REACT_APP_ABOUTUS_INTRO || "";
  const themeColor = config.REACT_APP_BACKGROUND_COLOR || "#000000";

  // Update favicon
  const faviconEl = document.getElementById("dynamic-favicon");
  if (faviconEl && mediaSrc) faviconEl.href = `${mediaSrc}favicon.ico`;

  // Update apple touch icon
  const appleIconEl = document.getElementById("dynamic-apple-icon");
  if (appleIconEl && mediaSrc) appleIconEl.href = `${mediaSrc}logo192.png`;

  // Update page title
  if (siteName) document.title = siteName;

  // Update theme color
  const themeColorEl = document.querySelector('meta[name="theme-color"]');
  if (themeColorEl) themeColorEl.content = themeColor;

  // Update Open Graph meta tags
  const setMeta = (id, value) => {
    const el = document.getElementById(id);
    if (el && value) el.content = value;
  };
  setMeta("meta-og-url", siteUrl);
  setMeta("meta-og-title", siteName);
  setMeta("meta-og-description", aboutUs);
  setMeta("meta-og-image", `${mediaSrc}logo.webp`);
  setMeta("meta-twitter-url", siteUrl);
  setMeta("meta-twitter-title", siteName);
  setMeta("meta-twitter-description", aboutUs);
  setMeta("meta-twitter-image", `${mediaSrc}logo.webp`);

  // Generate dynamic manifest
  if (mediaSrc) {
    const manifest = {
      short_name: siteName,
      name: siteName,
      icons: [
        { src: `${mediaSrc}logo192.png`, sizes: "192x192", type: "image/png", purpose: "maskable" },
        { src: `${mediaSrc}logo512.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
        { src: `${mediaSrc}favicon-32x32.png`, sizes: "32x32", type: "image/png", purpose: "maskable" },
        { src: `${mediaSrc}favicon-96x96.png`, sizes: "96x96", type: "image/png", purpose: "maskable" },
      ],
      start_url: "/",
      display: "standalone",
      theme_color: themeColor,
      background_color: "#ffffff",
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/json" });
    const manifestUrl = URL.createObjectURL(blob);
    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = manifestUrl;
    } else {
      manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      manifestLink.href = manifestUrl;
      document.head.appendChild(manifestLink);
    }
  }
}

async function bootstrap() {
  let apiData = {};
  try {
    apiData = await fetchSiteConfig(`${newApiBaseUrl}/v1/site-config?domain=${siteDomain}`);
    if (!apiData || Object.keys(apiData).length === 0) {
      console.warn("No config for domain, falling back to default site ID:", DEFAULT_SITE_ID);
      apiData = await fetchSiteConfig(`${newApiBaseUrl}/v1/${DEFAULT_SITE_ID}/site-config`);
    }
  } catch (err) {
    console.warn("Failed to load site config from API, using .env fallback:", err.message);
  }
  // API data takes priority; .env values (baked in at build time) serve as fallback
  initSiteConfig(apiData, envDefaults);

  // Dynamically update favicon, icons, manifest, and meta tags
  updateSiteAssets(apiData);

  const { default: App } = await import("./App");

  ReactDOM.render(
    <Provider store={store}>
      <Router>
        <Theme>
          <App />
        </Theme>
      </Router>
    </Provider>,
    rootElement
  );

  serviceWorker.unregister();
  swDev();
}

bootstrap();
